import { useMemo } from 'react';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters, useQuery } from '@tanstack/react-query';
import { logError } from '../../../../../components/LogError';
import {
  fetchReturnValuations,
  ReturnValuationsResponse,
  ReturnValuationData,
} from '../../../../../services/returnValuationsApi';

export interface ReturnValuationPerformance {
  date: string;
  currentHoldings: number;
  netContributions: number;
  netPosition: number | null;
  returnPct: number | null;
  oneMonthReturn: number | null;
}

interface ReturnValuationsSummary {
  inflows: number;
  outflows: number;
  refund: number;
  transferOut: number;
  netPosition: number;
  netPositionPct: number;
  netCapitalInvested: number; // SUM of cumNetCapitalInvested for the latest date per portfolio
  portfolioValue: number; // SUM of valuationEnd for the latest date
}

interface UseReturnValuationsResponse {
  returnValuations: ReturnValuationPerformance[];
  summary: ReturnValuationsSummary;
  loading: boolean;
  error: unknown;
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
  ) => Promise<QueryObserverResult<ReturnValuationsResponse | null, unknown>>;
}

type PortfolioIdParam = string | number | null | undefined;

/**
 * Hook to fetch return valuations for the Performance Story chart.
 * Accepts accountHolderId from parent to avoid sequential fetch dependency.
 * Uses two-phase loading: quick (100 points) for fast chart render, full (500) in parallel for complete history.
 */
export const useReturnValuations = (
  portfolioId: PortfolioIdParam,
  accountHolderId?: number | null,
): UseReturnValuationsResponse => {
  // Phase 1: Fast initial load with 100 data points (enough for 5Y view)
  const {
    data: quickData,
    isLoading: loadingQuick,
    error: quickError,
    refetch,
  } = useQuery({
    queryFn: () => {
      if (!accountHolderId) return Promise.resolve(null);
      return fetchReturnValuations({
        acctHolderId: accountHolderId,
        portfolioId: portfolioId ? String(portfolioId) : undefined,
        pageSize: 100,
      });
    },
    queryKey: ['fetchReturnValuations', accountHolderId, portfolioId, 'quick'],
    enabled: !!accountHolderId,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  // Phase 2: Full data in parallel (covers entire portfolio history for "All" view)
  const { data: fullData, error: fullError } = useQuery({
    queryFn: () => {
      if (!accountHolderId) return Promise.resolve(null);
      return fetchReturnValuations({
        acctHolderId: accountHolderId,
        portfolioId: portfolioId ? String(portfolioId) : undefined,
        pageSize: 500,
      });
    },
    queryKey: ['fetchReturnValuations', accountHolderId, portfolioId, 'full'],
    enabled: !!accountHolderId && !loadingQuick, // Start after Phase 1 finishes to reduce connection contention
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  // Use full data when available, otherwise quick data
  const returnValuationsData = fullData || quickData;

  const error = quickError || fullError;

  if (error) {
    logError(JSON.stringify(error));
  }

  // Check if we're viewing all portfolios (need to aggregate by date)
  const isAllPortfolios = !portfolioId || portfolioId === 'all' || portfolioId === '';

  // Transform return valuations to performance format
  const { returnValuations, summary } = useMemo(() => {
    const rawData: ReturnValuationData[] = returnValuationsData?.data || [];

    // Filter out records with null NAV values
    const validData = rawData.filter((item) => item.nav !== null && item.nav !== undefined);

    const defaultSummary: ReturnValuationsSummary = {
      inflows: 0,
      outflows: 0,
      refund: 0,
      transferOut: 0,
      netPosition: 0,
      netPositionPct: 0,
      netCapitalInvested: 0,
      portfolioValue: 0,
    };

    if (validData.length === 0) return { returnValuations: [], summary: defaultSummary };

    // Calculate portfolioValue: SUM of valuationEnd for the latest date
    const latestDate = validData.reduce((latest, item) => {
      const itemDate = new Date(item.valuationDate).getTime();
      return itemDate > latest ? itemDate : latest;
    }, 0);
    const latestDateStr = new Date(latestDate).toISOString().split('T')[0];
    const portfolioValue = validData
      .filter((item) => item.valuationDate.startsWith(latestDateStr))
      .reduce((sum, item) => sum + (item.valuationEnd ?? 0), 0);

    // If viewing all portfolios, aggregate (sum) by date
    if (isAllPortfolios) {
      // Get the last outflowAccum, inflowAccum, and cumNetCapitalInvested per portfolio (cumulative values)
      const lastPerPortfolio = new Map<
        string,
        { date: number; outflowAccum: number; inflowAccum: number; cumNetCapitalInvested: number }
      >();
      validData.forEach((item) => {
        const existing = lastPerPortfolio.get(item.portfolioId);
        const itemDate = new Date(item.valuationDate).getTime();
        if (!existing || itemDate > existing.date) {
          lastPerPortfolio.set(item.portfolioId, {
            date: itemDate,
            outflowAccum: item.outflowAccum ?? 0,
            inflowAccum: item.inflowAccum ?? 0,
            cumNetCapitalInvested: item.cumNetCapitalInvested ?? 0,
          });
        }
      });
      const totalOutflowAccum = Array.from(lastPerPortfolio.values()).reduce((sum, v) => sum + v.outflowAccum, 0);
      const totalInflowAccum = Array.from(lastPerPortfolio.values()).reduce((sum, v) => sum + v.inflowAccum, 0);
      const totalCumNetCapitalInvested = Array.from(lastPerPortfolio.values()).reduce(
        (sum, v) => sum + v.cumNetCapitalInvested,
        0,
      );

      // Group by date
      const groupedByDate = new Map<
        string,
        {
          nav: number;
          cumNetCapitalInvested: number;
          netPosition: number;
          inflowAccum: number;
          outflowAccum: number;
          refund: number;
          transferOut: number;
          count: number;
        }
      >();

      validData.forEach((item) => {
        const existing = groupedByDate.get(item.valuationDate);
        if (existing) {
          existing.nav += item.nav ?? 0;
          existing.cumNetCapitalInvested += item.cumNetCapitalInvested ?? 0;
          existing.netPosition += item.netPosition ?? 0;
          existing.inflowAccum += item.inflowAccum ?? 0;
          existing.outflowAccum += item.outflowAccum ?? 0;
          existing.refund += item.refund ?? 0;
          existing.transferOut += item.transferOut ?? 0;
          existing.count += 1;
        } else {
          groupedByDate.set(item.valuationDate, {
            nav: item.nav ?? 0,
            cumNetCapitalInvested: item.cumNetCapitalInvested ?? 0,
            netPosition: item.netPosition ?? 0,
            inflowAccum: item.inflowAccum ?? 0,
            outflowAccum: item.outflowAccum ?? 0,
            refund: item.refund ?? 0,
            transferOut: item.transferOut ?? 0,
            count: 1,
          });
        }
      });

      // Convert to array and sort by date
      const aggregatedData = Array.from(groupedByDate.entries())
        .map(([date, values]) => {
          const netCapital = values.cumNetCapitalInvested;
          const nav = values.nav;
          // Calculate return percentage from aggregated values
          const returnPct = netCapital > 0 ? ((nav - netCapital) / netCapital) * 100 : null;

          return {
            date,
            currentHoldings: nav,
            netContributions: netCapital,
            netPosition: values.netPosition,
            returnPct,
            oneMonthReturn: null, // Can't aggregate this meaningfully
            inflowAccum: values.inflowAccum,
            outflowAccum: values.outflowAccum,
            refund: values.refund,
            transferOut: values.transferOut,
          };
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Get summary from the latest data point
      const latestData = aggregatedData[aggregatedData.length - 1];
      const netCapitalInvested = totalInflowAccum - totalOutflowAccum;
      const aggregatedSummary: ReturnValuationsSummary = latestData
        ? {
            inflows: totalInflowAccum,
            outflows: totalOutflowAccum,
            refund: latestData.refund,
            transferOut: latestData.transferOut,
            netPosition: latestData.netPosition ?? 0,
            netPositionPct: totalInflowAccum !== 0 ? ((latestData.netPosition ?? 0) / totalInflowAccum) * 100 : 0,
            netCapitalInvested: totalCumNetCapitalInvested,
            portfolioValue,
          }
        : defaultSummary;

      return { returnValuations: aggregatedData, summary: aggregatedSummary };
    }

    // Single portfolio - no aggregation needed
    const sortedData = [...validData].sort(
      (a, b) => new Date(a.valuationDate).getTime() - new Date(b.valuationDate).getTime(),
    );

    // Get summary from the latest data point
    const latestItem = sortedData[sortedData.length - 1];
    const refundVal = latestItem?.refund ?? 0;
    const transferOutVal = latestItem?.transferOut ?? 0;
    const singleNetCapitalInvested = (latestItem?.inflowAccum ?? 0) - (latestItem?.outflowAccum ?? 0);
    const singleSummary: ReturnValuationsSummary = latestItem
      ? {
          inflows: latestItem.inflowAccum ?? 0,
          outflows: latestItem.outflowAccum ?? 0,
          refund: refundVal,
          transferOut: transferOutVal,
          netPosition: latestItem.netPosition ?? 0,
          netPositionPct:
            (latestItem.inflowAccum ?? 0) !== 0
              ? ((latestItem.netPosition ?? 0) / (latestItem.inflowAccum ?? 0)) * 100
              : 0,
          netCapitalInvested: latestItem.cumNetCapitalInvested ?? 0,
          portfolioValue,
        }
      : defaultSummary;

    const valuations = sortedData.map((item) => ({
      date: item.valuationDate,
      // Use nav as the NAV/current holdings value (blue line in chart)
      currentHoldings: item.nav ?? 0,
      // Use cumulative net capital invested (green line in chart)
      netContributions: item.cumNetCapitalInvested,
      // Net position from the API (orange bars in chart)
      netPosition: item.netPosition,
      // Return percentage from the API
      returnPct: item.totalNetPositionPercent,
      // One month return
      oneMonthReturn: item.oneMonthHoldingReturn,
    }));

    return { returnValuations: valuations, summary: singleSummary };
  }, [returnValuationsData, isAllPortfolios]);

  const loading = loadingQuick;

  return { returnValuations, summary, loading, error, refetch };
};
